// src/pages/BlogPage.tsx
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { getBlogPosts } from '../lib/blog';
import type { BlogPost } from '../types';
import { BlogCard } from '../components/BlogCard';
import { BlogSidebar } from '../components/BlogSidebar';
import { GlassCTA } from '../components/GlassCTA';
import { BlogCTA } from '../components/BlogCTA';

export function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadPosts() {
      try {
        const fetchedPosts = await getBlogPosts();
        setPosts(fetchedPosts);
      } catch (err) {
        console.error(err);
        setError('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    }

    loadPosts();
  }, []);

  const featuredPost = posts[0];
  const recentPosts = posts.slice(1);

  return (
    <div className="min-h-screen bg-gray-950 text-yellow-100">
      <Helmet>
        <title>Prop Viper Blog - Expert Trading Insights & News</title>
        <meta
          name="description"
          content="Read expert analysis, trading tips, and prop firm reviews to help you succeed in your trading journey."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-black to-gray-900 py-16 sm:py-20 text-center px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 text-yellow-400">Welcome to the Prop Viper Blog</h1>
          <p className="text-base sm:text-lg text-yellow-200 mb-6">
            Expert trading strategies, prop firm reviews, and tips to help you become a consistently funded trader.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 flex flex-col lg:flex-row gap-10" id="articles">
        <div className="lg:w-3/4">
          {/* Featured Article */}
          {featuredPost && (
            <div className="mb-16">
              <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-yellow-300">Featured Article</h2>
              <BlogCard post={featuredPost} />
              <div className="h-8" />
              <div className="mt-8">
                <GlassCTA variant="primary" position="top" promoUsedElsewhere={false} />
              </div>
            </div>
          )}

          {/* Recent Articles */}
          {recentPosts.length > 0 && (
            <div className="mb-16">
              <h2 className="text-xl sm:text-2xl font-bold mb-6 text-yellow-300">Latest Posts</h2>
              <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
                {recentPosts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-16">
            <div className="mt-8">
              <GlassCTA variant="secondary" position="bottom" promoUsedElsewhere={true} />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:w-1/4 w-full">
          <BlogSidebar />
        </aside>
      </div>
    </div>
  );
}
